import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PlusIcon, SearchIcon, XIcon } from 'lucide-react'
import {
	Button, ConfirmDialog, IconButton,
	Dialog, DialogContent, DialogHeader,
	Input,
} from '@rentflowdev/ui'
import {
	useAssignSubAccountPropertyMutation,
	useListPropertiesQuery,
	useUnassignSubAccountPropertyMutation,
} from '@/api'
import { memberRoleName } from '@/lib/use-member-roles'
import { formatPropertyAddress } from '@/lib/property-address'
import type { SubAccount } from '@/types'

interface AssignedPropertiesDialogProps {
	subAccount:   SubAccount | null
	open:         boolean
	onOpenChange: (open: boolean) => void
}

export function AssignedPropertiesDialog({ subAccount, open, onOpenChange }: AssignedPropertiesDialogProps) {
	const { t } = useTranslation()
	const [pickerOpen, setPickerOpen] = useState(false)
	const [pickerQuery, setPickerQuery] = useState('')
	const [confirmUnassign, setConfirmUnassign] = useState<{ propertyId: string; title: string } | null>(null)
	const { data: propertiesData } = useListPropertiesQuery({ limit: 100 }, { skip: !pickerOpen })
	const [assign,   { isLoading: isAssigning   }] = useAssignSubAccountPropertyMutation()
	const [unassign, { isLoading: isUnassigning }] = useUnassignSubAccountPropertyMutation()

	const assigned = subAccount?.properties ?? []
	const assignedIds = new Set(assigned.map((p) => p.propertyId))
	const candidatesAll = (propertiesData?.properties ?? []).filter((p) => !assignedIds.has(p.id))
	const candidates = useMemo(() => {
		const q = pickerQuery.trim().toLowerCase()
		if (!q) return candidatesAll
		return candidatesAll.filter((p) => {
			const haystack = `${p.city ?? ''} ${p.address ?? ''}`.toLowerCase()
			return haystack.includes(q)
		})
	}, [candidatesAll, pickerQuery])

	if (!subAccount) return null

	async function handleAssign(propertyId: string) {
		try {
			await assign({ id: subAccount!.id, body: { propertyId } }).unwrap()
			setPickerOpen(false)
		} catch (err) {
			console.error('assignSubAccountProperty failed:', err)
		}
	}

	async function handleUnassign(propertyId: string) {
		try {
			await unassign({ id: subAccount!.id, propertyId }).unwrap()
		} catch (err) {
			console.error('unassignSubAccountProperty failed:', err)
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				showCloseButton={false}
				className="flex flex-col gap-0 overflow-hidden p-0 rounded-(--radius-lg)"
				style={{ width: 560, maxWidth: '95vw', maxHeight: '85vh' }}
			>
				<DialogHeader
					border
					title={t('settings.team.assign.title')}
					subtitle={`${memberRoleName(subAccount)} ${subAccount.fullName}`}
				/>

				<div className="flex-1 overflow-auto px-6 py-4">
					{assigned.length === 0 ? (
						<p className="py-8 text-center text-sm text-(--color-muted-foreground)">
							{t('settings.team.assign.empty')}
						</p>
					) : (
						<ul className="flex flex-col gap-2">
							{assigned.map((p) => (
								<li
									key={p.propertyId}
									className="flex items-center gap-3 rounded-(--radius-md) border border-(--color-border) p-3"
								>
									<div className="size-12 shrink-0 overflow-hidden rounded-(--radius-md) bg-(--color-muted)">
										{p.imageUrl && <img src={p.imageUrl} alt="" className="size-full object-cover" />}
									</div>
									<div className="flex flex-1 flex-col gap-0.5">
										<span className="text-sm font-[500] text-(--color-foreground)">{p.title ?? '—'}</span>
										<span className="text-xs text-(--color-muted-foreground)">{p.address}</span>
									</div>
									<IconButton
										variant="ghost"
										size="sm"
										aria-label="Remove"
										onClick={() => setConfirmUnassign({ propertyId: p.propertyId, title: p.title ?? p.address ?? '' })}
										disabled={isUnassigning}
									>
										<XIcon className="size-4" />
									</IconButton>
								</li>
							))}
						</ul>
					)}
				</div>

				<div className="border-t border-(--color-border) bg-(--color-muted)/30 px-6 py-4">
					{pickerOpen ? (
						<div className="flex flex-col gap-2">
							<div className="relative">
								<SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-(--color-muted-foreground)" />
								<Input
									autoFocus
									placeholder={t('settings.team.assign.searchPlaceholder')}
									value={pickerQuery}
									onChange={(e) => setPickerQuery(e.target.value)}
									className="pl-9"
								/>
							</div>
							<ul className="flex max-h-72 flex-col gap-1 overflow-auto rounded-(--radius-md) bg-(--color-background) p-1">
								{candidates.length === 0 ? (
									<li className="px-2 py-3 text-center text-sm text-(--color-muted-foreground)">
										{candidatesAll.length === 0
											? t('settings.team.assign.allAssigned')
											: t('settings.team.assign.noMatches')}
									</li>
								) : (
									candidates.map((p) => (
										<li key={p.id}>
											<button
												type="button"
												onClick={() => void handleAssign(p.id)}
												disabled={isAssigning}
												className="w-full rounded-(--radius-sm) px-2 py-2 text-left text-sm hover:bg-(--color-muted)"
											>
												{formatPropertyAddress(p.city, p.address)}
											</button>
										</li>
									))
								)}
							</ul>
							<Button
								type="button"
								variant="ghost"
								size="sm"
								onClick={() => {
									setPickerOpen(false)
									setPickerQuery('')
								}}
							>
								{t('common.cancel')}
							</Button>
						</div>
					) : (
						<Button
							type="button"
							variant="ghost"
							className="w-full"
							onClick={() => setPickerOpen(true)}
							iconRight={<PlusIcon className="size-4" />}
						>
							{t('settings.team.assign.add')}
						</Button>
					)}
				</div>
			</DialogContent>

			<ConfirmDialog
				open={confirmUnassign !== null}
				onOpenChange={(next) => { if (!next) setConfirmUnassign(null) }}
				title={t('settings.team.unassignDialog.title')}
				description={t('settings.team.unassignDialog.description', { title: confirmUnassign?.title ?? '' })}
				cancelLabel={t('common.cancel')}
				confirmLabel={t('settings.team.unassignDialog.confirm')}
				variant="destructive"
				onConfirm={() => {
					if (confirmUnassign) void handleUnassign(confirmUnassign.propertyId)
					setConfirmUnassign(null)
				}}
			/>
		</Dialog>
	)
}
